import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEO from '../../components/SEO';

export default function ServicesPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://trigreensoul.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Organic Food Distribution and Consulting",
    "provider": {
      "@type": "Organization",
      "name": "TRIGREENSOUL PRIVATE CUSTOM SERVICES, S.L.",
      "url": siteUrl
    },
    "areaServed": {
      "@type": "Place",
      "name": "Europe"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Organic Agriculture Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Distribution & Logistics",
            "description": "Temperature-controlled transportation and seamless supply chain management ensuring fresh organic produce reaches your business on time."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "International Trade",
            "description": "Expert navigation of international markets, customs, and regulations for smooth cross-border organic trade."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Organic Crop Consulting",
            "description": "Professional advisory services for farmers and businesses transitioning to or optimizing organic production."
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Services - TRIGREENSOUL | Organic Distribution, International Trade & Crop Consulting"
        description="From farm to table, we provide comprehensive services that support every stage of the organic supply chain. Distribution & logistics, international trade, and expert organic crop consulting services."
        keywords="organic food distribution, international organic trade, organic crop consulting, organic supply chain, organic logistics, organic certification consulting, Barcelona organic services"
        canonical={`${siteUrl}/services`}
        schema={schema}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/ae0a5222-1684-471a-9ad4-134e3508a5e0.png" alt="TRIGREENSOUL" className="h-12 w-auto" />
            <span className="text-xl font-semibold text-gray-900">TRIGREENSOUL</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/about" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">About</Link>
            <Link to="/products" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">Products</Link>
            <Link to="/services" className="text-sm font-medium text-emerald-700">Services</Link>
            <Link to="/sustainability" className="text-sm font-medium text-gray-700 hover:text-emerald-600 transition-colors">Sustainability</Link>
            <Link to="/contact" className="px-6 py-2 rounded-full border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white transition-all whitespace-nowrap cursor-pointer">Partner With Us</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <span className="text-sm text-emerald-700 uppercase tracking-wider font-semibold">Our Services</span>
            <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mt-4 mb-8 leading-tight">
              End-to-End<br />Organic Solutions
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              From farm to table, we provide comprehensive services that support every stage of the organic supply chain, ensuring quality, sustainability, and business success.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Distribution & Logistics */}
          <div className="grid md:grid-cols-2 gap-16 items-center mb-32">
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://public.readdy.ai/ai/img_res/692548708bdcf1843d44cb4ae4689f1a.jpg" alt="Distribution center" className="w-full h-full object-cover object-center" />
            </div>
            <div>
              <div className="w-16 h-16 flex items-center justify-center mb-6 bg-emerald-100 rounded-2xl">
                <i className="ri-truck-line text-4xl text-emerald-700"></i>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Distribution & Logistics</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our state-of-the-art distribution network ensures your organic produce arrives fresh, on time, and in perfect condition. We manage the entire logistics chain with precision and care.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Temperature-controlled transportation maintaining optimal freshness</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Real-time tracking and delivery updates for complete transparency</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Flexible delivery schedules tailored to your business needs</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Pan-European coverage with local expertise in each market</span>
                </li>
              </ul>
            </div>
          </div>

          {/* International Trade */}
          <div className="grid md:grid-cols-2 gap-16 items-center mb-32">
            <div className="order-2 md:order-1">
              <div className="w-16 h-16 flex items-center justify-center mb-6 bg-emerald-100 rounded-2xl">
                <i className="ri-earth-line text-4xl text-emerald-700"></i>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">International Trade</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Navigate the complexities of international organic trade with confidence. Our expertise in cross-border commerce opens doors to new markets and opportunities.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Expert handling of customs, certifications, and regulatory compliance</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Market entry strategies for new geographic territories</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Documentation and paperwork management for seamless transactions</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Established relationships with international partners and authorities</span>
                </li>
              </ul>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl order-1 md:order-2">
              <img src="https://readdy.ai/api/search-image?query=Global%20logistics%20concept%20with%20cargo%20containers%20and%20world%20map%20showing%20international%20trade%20routes%20and%20shipping%20networks%20with%20professional%20business%20environment%20emphasizing%20global%20commerce%20and%20supply%20chain%20management%20in%20clean%20modern%20setting&width=900&height=700&seq=trade001&orientation=landscape" alt="International trade" className="w-full h-full object-cover object-center" />
            </div>
          </div>

          {/* Organic Crop Consulting */}
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://readdy.ai/api/search-image?query=Agricultural%20consultant%20examining%20organic%20crops%20in%20field%20with%20tablet%20showing%20data%20analysis%20and%20sustainable%20farming%20practices%20with%20professional%20agronomist%20in%20natural%20outdoor%20setting%20emphasizing%20expertise%20and%20modern%20agriculture%20technology&width=900&height=700&seq=consult001&orientation=landscape" alt="Crop consulting" className="w-full h-full object-cover object-center" />
            </div>
            <div>
              <div className="w-16 h-16 flex items-center justify-center mb-6 bg-emerald-100 rounded-2xl">
                <i className="ri-plant-line text-4xl text-emerald-700"></i>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Organic Crop Consulting</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Transform your agricultural operations with expert guidance from our team of certified agronomists and organic farming specialists.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Organic certification process guidance and support</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Sustainable crop management and soil health optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Pest and disease management using organic methods</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-emerald-700 flex-shrink-0"></i>
                  <span className="text-gray-700">Yield optimization while maintaining organic standards</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Additional Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive support services designed to help your organic business thrive
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-emerald-100 rounded-xl">
                <i className="ri-bar-chart-box-line text-3xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Market Analysis</h3>
              <p className="text-gray-600 leading-relaxed">
                In-depth market research and trend analysis to help you make informed business decisions and identify new opportunities in the organic sector.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-emerald-100 rounded-xl">
                <i className="ri-shield-check-line text-3xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality Assurance</h3>
              <p className="text-gray-600 leading-relaxed">
                Rigorous quality control protocols and testing services ensuring every product meets the highest organic certification standards and customer expectations.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-emerald-100 rounded-xl">
                <i className="ri-team-line text-3xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Training Programs</h3>
              <p className="text-gray-600 leading-relaxed">
                Educational workshops and training sessions on organic farming practices, certification requirements, and sustainable agriculture techniques.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-emerald-100 rounded-xl">
                <i className="ri-file-text-line text-3xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Documentation Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete assistance with certification paperwork, compliance documentation, and regulatory filings for organic operations.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-emerald-100 rounded-xl">
                <i className="ri-customer-service-2-line text-3xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Dedicated Account Management</h3>
              <p className="text-gray-600 leading-relaxed">
                Personal account managers who understand your business needs and provide tailored solutions and ongoing support.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-emerald-100 rounded-xl">
                <i className="ri-line-chart-line text-3xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Business Development</h3>
              <p className="text-gray-600 leading-relaxed">
                Strategic planning and growth consulting to help expand your organic business and reach new customer segments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">How We Work</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our streamlined process ensures seamless collaboration and exceptional results
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-emerald-700 text-white rounded-full text-3xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Consultation</h3>
              <p className="text-gray-600">
                We discuss your needs, goals, and challenges to create a customized solution
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-emerald-700 text-white rounded-full text-3xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Planning</h3>
              <p className="text-gray-600">
                Our experts develop a detailed strategy tailored to your business requirements
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-emerald-700 text-white rounded-full text-3xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Implementation</h3>
              <p className="text-gray-600">
                We execute the plan with precision, keeping you informed every step of the way
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-emerald-700 text-white rounded-full text-3xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
              <p className="text-gray-600">
                Continuous monitoring and optimization to ensure long-term success
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-900 to-emerald-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold mb-6">Let's Discuss Your Needs</h2>
          <p className="text-xl text-emerald-100 mb-10 leading-relaxed">
            Contact our team to learn how our services can help your organic business grow and succeed in today's competitive market
          </p>
          <Link to="/contact" className="inline-block px-10 py-4 bg-white text-emerald-900 font-bold rounded-full hover:bg-emerald-50 transition-all hover:scale-105 cursor-pointer whitespace-nowrap">
            Schedule a Consultation →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-20 pb-12 mx-8 mt-16 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div>
              <h3 className="text-4xl font-light mb-4">Stay Connected</h3>
              <p className="text-gray-400 mb-8">Subscribe to organic industry insights</p>
            </div>
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-6">Quick Links</h4>
              <div className="space-y-4">
                <Link to="/about" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">About Us</Link>
                <Link to="/products" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Products</Link>
                <Link to="/sustainability" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Certifications</Link>
                <Link to="/contact" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-6">Contact</h4>
              <p className="text-white mb-8">info@trigreensoul.com</p>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-4">Headquarters</h4>
              <p className="text-white text-sm leading-relaxed">
                Barcelona, Spain<br />
                European Union
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2025 TRIGREENSOUL. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-emerald-400 transition-colors cursor-pointer text-sm">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
